import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { rbacVisibleItems, type RbacPolicy } from '@/shared/permissions/rbac';

export interface DropdownItem extends RbacPolicy {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
}

export function Dropdown({ trigger, items, align = 'right' }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = useUser();
  const visibleItems = rbacVisibleItems(items, user);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes the menu. Without it the only way out of an open flyout is
    // to find and click the page behind it, which is not operable from the
    // keyboard.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (visibleItems.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute z-40 mt-1 min-w-[180px] overflow-hidden rounded-lg border border-border bg-popover py-1 shadow-surface-2 animate-fade-in',
            align === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
          )}
        >
          {visibleItems.map((item, i) => (
            <button
              key={`${item.label}-${i}`}
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                item.onClick();
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-data-md transition-colors focus-visible:outline-none focus-visible:bg-muted disabled:opacity-50',
                item.danger
                  // text-danger-text, not text-danger: the 500-weight fill is a
                  // dot/bar colour and read 3.4:1 as menu text.
                  ? 'text-danger-text hover:bg-destructive-subtle'
                  : 'text-on-surface hover:bg-muted',
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
